import { apiRequest } from '@/utils/api-request';
import { PauseStateSnapshot } from '@/types/workflow-types';
import * as Sentry from '@sentry/react';

export interface NodeResult {
  id: string;
  node_id: string;
  node_instance_id: string | null; // React Flow node ID for frontend matching
  status: string;
  reason?: string | null;
  started_at: string | null;
  completed_at: string | null;
  output_data: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
}

export interface WorkflowRunWithResults {
  id: string;
  workflow_id: string;
  version_id: string;
  session_id: string;
  run_by_id: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  payload: {
    id: string;
    session_id: string;
    name: string;
    nodes: unknown[];
    edges: unknown[];
    description?: string;
    metadata?: Record<string, unknown>;
    lastExecution?: {
      timestamp: string;
      nodeStatuses: Record<string, unknown[]>;
    };
  } | null;
  node_results: NodeResult[];
  /**
   * Snapshot of the workflow's pause state when ``status === 'paused'``;
   * null otherwise.  Mirrors the JSONB persisted on
   * ``workflow_run.pause_state``.  Consumed by the editor's
   * ``handleReconnectToLiveRun`` callback to hydrate the canvas's
   * ``isPaused`` / ``pauseState`` flags after a page refresh, so
   * the resumed view looks identical to the pre-refresh view.
   */
  pause_state?: PauseStateSnapshot | null;
  /**
   * Pre-built WebSocket URL for this run's session, identical in
   * shape to what ``/session/create-session`` returns.  Provided by
   * the BE so the FE doesn't need to know about the BE's
   * ``SOCKET_ADDRESS`` env var when reconnecting to a still-running
   * (or paused) run.
   */
  session_socket_url?: string | null;
  /**
   * The node parked in a node chat waiting on the user, if any, for a
   * running or paused run -- the interactive-feedback family only; a Human
   * Intervention pause is `pause_state`. The live channel does not replay
   * history, so this is how a client reattaching after a refresh learns a
   * node is waiting.
   */
  awaiting_chat_node_id?: string | null;
  /**
   * The document that node put up for review, when it produced one. It
   * was only ever streamed, just before the pause, so this is the one
   * place a reattaching client can get it from.
   */
  awaiting_chat_review_url?: string | null;
}

export interface GetWorkflowRunResponse {
  data?: WorkflowRunWithResults;
  detail?: string;
}

/**
 * Fetches a specific run with its results from the v1 API.
 * @param workflowId - The workflow ID
 * @param runId - The run ID
 * @param orgId - The organization ID
 * @returns Promise containing the run with its results
 */
export async function getWorkflowRun(
  workflowId: string,
  runId: string,
  orgId: string,
): Promise<GetWorkflowRunResponse> {
  try {
    const response = await apiRequest<WorkflowRunWithResults>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/runs/${runId}`,
      target: 'workflow',
      method: 'GET',
    });

    if (response?.detail) {
      return {
        detail: response.detail || 'Failed to fetch workflow run details',
      };
    }

    // The response.data contains the actual response
    const data = response.data || response;
    return { data: data as WorkflowRunWithResults };
  } catch (error) {
    console.error('Error fetching workflow run:', error);
    Sentry.captureException(error, {
      extra: { context: 'get-workflow-run' },
    });
    throw error;
  }
}
